import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Search, ArrowLeft, ArrowRight, School, Award,
  BookOpen, Loader2, AlertCircle, ChevronRight, User, LogIn,
  PrinterIcon
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLOR = (score) => {
  if (score >= 75) return 'text-emerald-700 bg-emerald-50';
  if (score >= 60) return 'text-blue-700 bg-blue-50';
  if (score >= 50) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
};

function gradeLabel(score) {
  if (score >= 75) return 'A1';
  if (score >= 70) return 'B2';
  if (score >= 65) return 'B3';
  if (score >= 60) return 'C4';
  if (score >= 55) return 'C5';
  if (score >= 50) return 'C6';
  if (score >= 45) return 'D7';
  if (score >= 40) return 'E8';
  return 'F9';
}

export default function PublicResultChecker() {
  const navigate = useNavigate();

  const [step, setStep] = useState('phone'); // phone | list | result
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [result, setResult] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  const handlePhoneSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setErr('Please enter a phone number'); return; }
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/results/by-phone`, { phone });
      setStudents(r.data.data);
      if (r.data.data.length === 1 && r.data.data[0].terms?.length > 0) {
        setSelectedStudent(r.data.data[0]);
      }
      setStep('list');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Phone number not found. Please check with your school.');
    } finally { setLoading(false); }
  };

  const handleSelectTerm = async (student, term) => {
    setSelectedStudent(student);
    setSelectedTerm(term);
    setResultLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/api/public/results/${student.student_id}/${student.school_id}/${term.id}`);
      setResult(r.data.data);
      setStep('result');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load result. Please try again.');
    } finally { setResultLoading(false); }
  };

  const reset = () => {
    setStep('phone'); setPhone(''); setStudents([]);
    setSelectedStudent(null); setSelectedTerm(null); setResult(null); setErr('');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start pt-6 pb-12 px-4"
      style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #E0EEFF 50%, #F0F7FF 100%)' }}
    >
      {/* Top bar */}
      <div className="w-full max-w-lg flex items-center justify-between mb-6">
        <button
          onClick={() => step === 'phone' ? navigate('/') : (step === 'list' ? reset() : setStep('list'))}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#2563eb] font-semibold transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {step === 'phone' ? 'Home' : 'Back'}
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-xs font-black text-[#2563eb]"
        >
          <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}>
            <School className="w-3.5 h-3.5 text-white" />
          </div>
          EDUVA
        </button>
      </div>

      {/* ── Step: Phone ── */}
      {step === 'phone' && (
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl shadow-blue-100/60 overflow-hidden">
          <div className="px-8 pt-9 pb-5 text-center border-b border-slate-50">
            <div
              className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
            >
              <Award className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-black text-slate-900">Check Result</h1>
            <p className="text-slate-500 text-sm mt-1 max-w-xs mx-auto">
              Enter the phone number used when registering your child with their school.
            </p>
          </div>

          <form onSubmit={handlePhoneSearch} className="px-8 py-6 space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5">Parent Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setErr(''); }}
                  placeholder="e.g. 08074272239"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] text-sm transition-all"
                  autoFocus
                  data-testid="check-result-phone-input"
                />
              </div>
              {err && (
                <div className="flex items-start gap-2 mt-2 p-3 bg-red-50 rounded-xl border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-xs">{err}</p>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
              data-testid="check-result-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4" /> Search</>}
            </button>
          </form>

          <div className="px-8 pb-7 text-center">
            <p className="text-xs text-slate-400">
              Only current session results are shown here. &nbsp;
              <button onClick={() => navigate('/')} className="text-[#2563eb] font-semibold hover:underline">
                Login for full history
              </button>
            </p>
          </div>
        </div>
      )}

      {/* ── Step: Student/Term List ── */}
      {step === 'list' && (
        <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl shadow-blue-100/60 overflow-hidden">
          <div className="px-6 pt-6 pb-4 border-b border-slate-100">
            <h2 className="font-black text-slate-900 text-lg">Select Result to View</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {students.length} student{students.length !== 1 ? 's' : ''} found for <span className="font-bold text-slate-700">{phone}</span>
            </p>
          </div>

          {resultLoading && (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          )}

          {err && (
            <div className="mx-6 my-4 flex gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-600 text-xs">{err}</p>
            </div>
          )}

          <div className="p-4 space-y-4">
            {students.map((s) => (
              <div key={s.student_id} className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden">
                {/* Student header */}
                <div className="flex items-center gap-3 p-4 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-2xl bg-[#2563eb] flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                    {s.student_name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-800 text-sm">{s.student_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {s.class_name && <span className="text-xs text-slate-500">{s.class_name}</span>}
                      {s.class_name && s.school_name && <span className="text-slate-300 text-xs">·</span>}
                      {s.school_name && (
                        <div className="flex items-center gap-1">
                          <School className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500">{s.school_name}</span>
                        </div>
                      )}
                    </div>
                    {s.current_session && <p className="text-xs text-[#2563eb] font-semibold mt-0.5">{s.current_session}</p>}
                  </div>
                </div>

                {/* Term list */}
                {s.terms?.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {s.terms.map(term => (
                      <button
                        key={term.id}
                        onClick={() => handleSelectTerm(s, term)}
                        disabled={resultLoading}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white transition-colors group"
                        data-testid={`term-${term.id}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-[#2563eb] transition-colors" />
                          <span className="text-sm font-semibold text-slate-700 group-hover:text-[#2563eb] transition-colors">{term.name}</span>
                          {term.is_current && (
                            <span className="text-[10px] bg-blue-100 text-[#2563eb] font-bold px-2 py-0.5 rounded-full">Current</span>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#2563eb] group-hover:translate-x-0.5 transition-all" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center text-sm text-slate-400">No terms available for this student yet</div>
                )}
              </div>
            ))}
          </div>

          <div className="px-6 pb-6">
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 border border-[#2563eb] text-[#2563eb] font-bold text-sm rounded-xl flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors"
            >
              <LogIn className="w-4 h-4" /> Login for full history
            </button>
          </div>
        </div>
      )}

      {/* ── Step: Result ── */}
      {step === 'result' && result && (
        <div className="w-full max-w-lg" data-testid="public-result-card">
          {/* Result Card */}
          <div className="bg-white rounded-3xl shadow-2xl shadow-blue-100/60 overflow-hidden print:shadow-none">
            {/* Header */}
            <div
              className="px-6 py-5 text-white print:text-black"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <School className="w-5 h-5 text-white/80" />
                  <p className="font-bold text-sm text-white/90">{result.school_name}</p>
                </div>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-white/30 transition-colors no-print"
                >
                  <PrinterIcon className="w-3.5 h-3.5" /> Print
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center font-black text-xl">
                  {result.student?.first_name?.charAt(0)}
                </div>
                <div>
                  <p className="font-black text-xl">{result.student?.first_name} {result.student?.last_name}</p>
                  <p className="text-white/70 text-sm">{result.student?.class_name} &nbsp;·&nbsp; {selectedTerm?.name}</p>
                </div>
              </div>
            </div>

            {!result.published ? (
              <div className="px-6 py-10 text-center">
                <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="font-bold text-slate-700">Results Not Yet Published</p>
                <p className="text-sm text-slate-500 mt-1">{result.message || 'The school has not published results for this term yet.'}</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
                  {[
                    { label: 'Average', value: `${result.average ?? '--'}%` },
                    { label: 'Position', value: result.position ? `${result.position}${['st','nd','rd'][result.position-1]||'th'}` : '--' },
                    { label: 'Class Size', value: result.class_size || '--' },
                  ].map(({ label, value }) => (
                    <div key={label} className="py-4 px-5 text-center">
                      <p className="text-lg font-black text-[#2563eb]">{value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Subjects */}
                {result.subjects?.length > 0 && (
                  <div className="px-5 py-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Subject Results</p>
                    <div className="space-y-2">
                      {result.subjects.map((s, i) => (
                        <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                          <p className="text-sm font-semibold text-slate-700">{s.subject}</p>
                          <div className="flex items-center gap-3">
                            <div className="w-28 bg-slate-100 rounded-full h-1.5">
                              <div
                                className="h-1.5 rounded-full bg-[#2563eb]"
                                style={{ width: `${Math.min((s.total_score || s.score || 0), 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-slate-800 w-8 text-right">{s.total_score ?? s.score ?? '--'}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-8 text-center ${GRADE_COLOR(s.total_score ?? s.score ?? 0)}`}>
                              {gradeLabel(s.total_score ?? s.score ?? 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Remarks */}
                {(result.teacher_remark || result.principal_remark) && (
                  <div className="px-5 pb-5 space-y-3">
                    {result.teacher_remark && (
                      <div className="bg-slate-50 rounded-2xl p-4">
                        <p className="text-xs font-bold text-slate-500 mb-1">Class Teacher's Remark</p>
                        <p className="text-sm text-slate-700 italic">"{result.teacher_remark}"</p>
                      </div>
                    )}
                    {result.principal_remark && (
                      <div className="bg-blue-50 rounded-2xl p-4">
                        <p className="text-xs font-bold text-[#2563eb] mb-1">Principal's Remark</p>
                        <p className="text-sm text-slate-700 italic">"{result.principal_remark}"</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* CTA */}
            <div className="px-5 pb-5 pt-2 border-t border-slate-100 no-print">
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
              >
                <LogIn className="w-4 h-4" /> Login for full history & portal access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
